import {
  CHECKOUT_ITEM_TYPE_TOUR_V2,
} from 'constants/checkout'
import extractTourV2PurchasableOptionDetails from 'lib/tours/extractTourV2PurchasableOptionDetails'
import { getItemUniqueKey, getPackageId } from '../accommodation/cart'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import generateDepartureRoomPriceBreakdown from 'lib/tours/generateDepartureRoomPriceBreakdown'
import { capitalise } from 'lib/string/stringUtils'
import { getLabels } from '../accommodation/label'
import {
  LOYALTY_TOUR_CAPPED_EARN_POINTS_TOTAL,
  LOYALTY_TOUR_CAPPED_EARN_STATUS_CREDITS_TOTAL,
} from 'loyalty/constants/loyaltyEarn'
import { IntlShape } from 'react-intl'

function dummyAccommodationItemView(
  intl: IntlShape,
  item: App.Checkout.TourV2Item,
  kind: 'tourV2',
  offer?: App.Tours.TourV2Offer,
): App.Checkout.TourV2AccommodationItemView {
  return {
    title: '',
    kind,
    offerId: item.offerId,
    packageId: getPackageId(item),
    uniqueKey: getItemUniqueKey(item),
    item,
    loyalty: {
      productType: getLoyaltyProductType(offer),
    },
    primaryTitle: '',
    occupancy: { adults: 0, children: 0, infants: 0 },
    removeLabel: intl.formatMessage({
      id: 'HeRroSE1/H',
      defaultMessage: 'Remove',
      description: 'Remove button label for tour item in checkout',
    }),
    totals: {
      price: 0,
      memberPrice: 0,
      value: 0,
      surcharge: 0,
      taxesAndFees: 0,
      memberValue: 0,
      extraGuestSurcharge: 0,
    },
  }
}

export function getTourV2ItemView(
  intl: IntlShape,
  item: App.Checkout.TourV2Item,
  index: number,
  offer?: App.Tours.TourV2Offer,
  postPurchase?: App.CheckoutCartMode,
  existingOrder?: App.Order,
): App.WithDataStatus<App.Checkout.TourV2AccommodationItemView> {
  if (!offer) {
    return {
      hasRequiredData: false,
      data: dummyAccommodationItemView(intl, item, CHECKOUT_ITEM_TYPE_TOUR_V2),
    }
  }

  const {
    roomTypeName,
    valuedAt,
  } = extractTourV2PurchasableOptionDetails(item.purchasableOption)

  const variationId = item.purchasableOption.fkVariationId
  const variation = offer.variations[variationId] ?? Object.values(offer?.variations ?? {}).find(variation => variation.fkTourOptionId === variationId)
  const packageOptionInclusion = variation?.availablePackageUpgrades?.find(
    packageUpgrade => packageUpgrade.packageOption.toLowerCase() === item.purchasableOption.packageType?.toLowerCase(),
  )

  const isTourDateChange = postPurchase === 'tour-change-dates'
  // only set paid price for the first item in the list on changes date because it's not splitable per room
  const paidPrice = isTourDateChange && index === 0 ? existingOrder?.tourItems?.[0].total : undefined
  const { totalPrice, totalMemberPrice } = generateDepartureRoomPriceBreakdown(
    item.occupancy,
    item.purchasableOption,
    offer,
  )

  const labels = getLabels(offer, variationId)

  const loyaltyProductType = getLoyaltyProductType(offer)
  let earnCap: App.Checkout.LoyaltyEarnCap | undefined

  if (loyaltyProductType === 'le_tour') {
    earnCap = {
      points: LOYALTY_TOUR_CAPPED_EARN_POINTS_TOTAL,
      statusCredits: LOYALTY_TOUR_CAPPED_EARN_STATUS_CREDITS_TOTAL,
    }
  }

  return {
    hasRequiredData: true,
    data: {
      title: `${capitalise(offer.saleUnit)} ${index + 1}: ${labels.mainLabel}`,
      kind: CHECKOUT_ITEM_TYPE_TOUR_V2,
      offerId: item.offerId,
      packageId: item.purchasableOption.fkDepartureId,
      uniqueKey: item.purchasableOption.fkDepartureId,
      item,
      primaryTitle: roomTypeName ?? '',
      occupancy: item.occupancy,
      removeLabel: intl.formatMessage({
        id: 'myXq1fL3RZ',
        defaultMessage: 'Remove room',
        description: 'Remove button label for tour room item in checkout',
      }),
      loyalty: {
        productType: loyaltyProductType,
        earnCap,
      },
      totals: {
        price: totalPrice,
        memberPrice: totalMemberPrice,
        value: valuedAt?.price ?? 0,
        surcharge: 0,
        taxesAndFees: 0,
        paidPrice,
        memberValue: 0,
        extraGuestSurcharge: 0,
      },
      variation,
      packageOptionInclusion,
    },
  }
}
